import React, { useState } from 'react'

import { Controller, UseFormReturn } from 'react-hook-form'

import { RegisterRequestDTO } from '@/core/api/dtos/request/auth/register'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { ErrorMessage } from '@/shared/components/atoms/ErrorMessage'
import { Input } from '@/shared/components/atoms/Input'
import { Label } from '@/shared/components/atoms/Label/Label'

interface RegisterStep4Props {
	form: UseFormReturn<RegisterRequestDTO>
	isLoading: boolean
	onNextStep: (formData: Partial<RegisterRequestDTO>) => void
	onPrevStep: () => void
}

export const RegisterStep4: React.FC<RegisterStep4Props> = ({
	form,
	isLoading,
	onNextStep,
}) => {
	const { t } = useTranslation('auth')
	// Track if user has started editing (to use local state vs form value)
	const [isEditing, setIsEditing] = useState(false)
	const [storesInputValue, setStoresInputValue] = useState<string>('')
	const [hasBlurred, setHasBlurred] = useState(false)

	const brandName = form.watch('brand_name') || ''
	const totalStores = form.watch('total_company_stores')

	const isFormValid =
		brandName.length > 0 &&
		totalStores !== undefined &&
		totalStores !== null &&
		totalStores >= 1 &&
		Object.keys(form.formState.errors).length === 0

	const handleNextStep = async () => {
		const isValid = await form.trigger(['brand_name', 'total_company_stores'])

		if (isValid) {
			const { brand_name, total_company_stores } = form.getValues()
			const stepFormData = {
				brand_name,
				total_company_stores,
			}
			onNextStep(stepFormData)
		}
	}

	return (
		<div className="space-y-6 flex flex-col justify-between h-full">
			<div>
				<h1 className="register-title font-bold mb-4">
					{t('register.step3Title')}
				</h1>
				<p className="mb-8">{t('register.step3Instructions')}</p>
				<div className="space-y-4">
					<div>
						<Label className="mb-2 block">{t('register.brandName')}</Label>
						<Controller
							control={form.control}
							name="brand_name"
							render={({ field, fieldState: { error } }) => (
								<div>
									<Input
										placeholder={t('register.brandName')}
										className="h-10 w-full"
										{...field}
									/>
									{error && <ErrorMessage error={error.message ?? ''} />}
								</div>
							)}
						/>
					</div>
					<div>
						<Label className="mb-2 block">
							{t('register.totalCompanyStores')}
						</Label>
						<Controller
							control={form.control}
							name="total_company_stores"
							render={({ field, fieldState: { error } }) => {
								// Use local state when editing, otherwise use form value
								const displayValue = isEditing
									? storesInputValue
									: field.value !== undefined
										? String(field.value)
										: ''

								// Only show error after blur
								const shouldShowError = error && hasBlurred

								return (
									<div>
										<Input
											type="text"
											inputMode="numeric"
											pattern="[0-9]*"
											placeholder={t('register.totalCompanyStoresPlaceholder')}
											className="h-10 w-full"
											value={displayValue}
											onChange={(e) => {
												const inputValue = e.target.value
												// Allow empty or numeric values only
												if (inputValue === '' || /^\d+$/.test(inputValue)) {
													setIsEditing(true)
													setStoresInputValue(inputValue)
													const numValue =
														inputValue === '' ? undefined : Number(inputValue)
													field.onChange(numValue)
												}
											}}
											onFocus={() => {
												// Initialize local state from form value when focusing
												if (!isEditing && field.value !== undefined) {
													setStoresInputValue(String(field.value))
												}
												setIsEditing(true)
												setHasBlurred(false)
											}}
											onBlur={() => {
												field.onBlur()
												setHasBlurred(true)
											}}
										/>
										{shouldShowError && (
											<ErrorMessage error={error.message ?? ''} />
										)}
									</div>
								)
							}}
						/>
					</div>
				</div>
			</div>

			<div className="mt-8 flex gap-4">
				<Button
					onClick={handleNextStep}
					className="px-8 py-3 cursor-pointer flex-1 h-10 text-sm font-medium rounded-full"
					disabled={isLoading || !isFormValid}
				>
					{isLoading ? 'Loading...' : t('register.following')}
				</Button>
			</div>
		</div>
	)
}
